/**
 * Accept a waiting invitation, then land where it belongs — in one click.
 * (W2 §9.2 · H15 · Citizen first run slice D)
 *
 * Accepting used to hand the citizen back to `/accept-invite?token=…` so that
 * page could own the routing; that page routes on ITS OWN accept mutation, and
 * on a fresh load of an already-accepted invitation it rendered the preview
 * again and waited for a second click. Both accept surfaces now ask the shared
 * first-destination resolver instead: the CheckUp for a `full_checkup`
 * invitation, TestIt for a `testit` one. (A citizen not yet set up has been
 * asked where they live before accepting, so the journey is already live.)
 *
 * A TestIt ask that was complete the moment it was accepted (D3) lands on
 * TestIt with the acceptance's own answer — the company and the results it now
 * has — which the page confirms instead of showing a list with nothing on it.
 */
import { resolvePostOnboardingDestination } from '@tonnie/onboarding';
import type { AcceptJourneyChoice, MyPendingInvitation } from '@tonnie/sdk';
import { useAcceptInvitation } from '@tonnie/sdk';
import { useCallback, useEffect, useRef, useState } from 'react';

import { testitSharedState, useGoToFirstDestination } from '@/features/platform/first-run';

type AcceptedInvitation = Pick<MyPendingInvitation, 'token' | 'invitationKind' | 'companyName'>;

export function useAcceptAndRoute() {
  const acceptMutation = useAcceptInvitation();
  const goToFirstDestination = useGoToFirstDestination();

  // The invitation being accepted, captured at click time. It cannot be read
  // off the list afterwards: acceptance removes the row, and a destination
  // resolved from a kind that has just become `undefined` would send a CheckUp
  // invitee to the dashboard.
  const [accepted, setAccepted] = useState<AcceptedInvitation | null>(null);
  const result = acceptMutation.data ?? null;

  // The journey passed in is the one THIS acceptance returned: the mutation
  // invalidates the journeys list, so reading that list here would race its
  // refetch and could answer from a snapshot taken before the journey existed.
  const routedRef = useRef(false);
  useEffect(() => {
    if (!acceptMutation.isSuccess || !result?.journeyId || !accepted) return;
    if (routedRef.current) return;
    routedRef.current = true;
    goToFirstDestination(
      resolvePostOnboardingDestination({
        invitationKind: accepted.invitationKind,
        journeys: [{ id: result.journeyId }],
      }),
      result.testit?.completed
        ? testitSharedState({ company: accepted.companyName, tests: result.testit.tests })
        : undefined
    );
  }, [acceptMutation.isSuccess, result, accepted, goToFirstDestination]);

  const accept = useCallback(
    (
      invitation: AcceptedInvitation,
      /** Absent for a TestIt invitation, which chooses no journey (D3). */
      journeyChoice?: AcceptJourneyChoice
    ) => {
      setAccepted(invitation);
      acceptMutation.mutate({ token: invitation.token, ...(journeyChoice && { journeyChoice }) });
    },
    [acceptMutation]
  );

  return {
    accept,
    /**
     * Still "accepting" after success, until the navigation unmounts the
     * surface: a button that stops spinning before anything moves reads as a
     * dead end.
     */
    isAccepting: acceptMutation.isPending || acceptMutation.isSuccess,
    isError: acceptMutation.isError,
    errorCode: (acceptMutation.error as { code?: string } | null)?.code,
  };
}
